/**
 * 🔊 SONS DES PRODUITS - Effets sonores fun pour la landing page
 * 
 * Chaque catégorie de produit a son propre son caractéristique !
 * - Red Bull : Pshiiit (canette)
 * - Vodka : Ting! (tchin/trinquer)
 * - Vin : Pop (bouchon)
 * - Champagne : POP! (bouchon qui saute fort)
 * - Whisky : Clic cristal
 * - Bière : Pshit mousseux
 * - CBD/FFF : Toux/fumée
 * - Croquettes : Aboiement petit chien
 * - Chat : Miaulement
 * - Biscuit : Croc croc
 */

// Créer un contexte audio
const createAudioContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch (e) {
    return null;
  }
};

// Générateur de bruit blanc (pour les effets de souffle)
const createWhiteNoise = (audioContext, duration) => {
  const bufferSize = audioContext.sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  const output = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    output[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// 🥤 PSHIIIT - Red Bull (canette qui s'ouvre)
const playRedBullSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Bruit blanc filtré pour le "pshiiit"
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.8);

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(3000, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(8000, ctx.currentTime + 0.1);
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.8);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 0.8);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍸 TING - Vodka (tchin! trinquer)
const playVodkaSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son cristallin de verre qui tinte
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(2500, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.3);

    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);

    // Deuxième harmonique
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(3800, ctx.currentTime);
    gain2.gain.setValueAtTime(0.1, ctx.currentTime);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime);
    osc2.stop(ctx.currentTime + 0.3);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍷 POP - Vin (bouchon qui saute doucement)
const playWineSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // "Pop" doux
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.15);

    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍾 POP! - Champagne (bouchon qui saute fort + pétillement)
const playChampagneSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // POP fort
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);

    // Pétillement après le pop
    setTimeout(() => {
      const noise = ctx.createBufferSource();
      noise.buffer = createWhiteNoise(ctx, 0.5);
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 5000;
      const gain2 = ctx.createGain();
      gain2.gain.setValueAtTime(0.08, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      noise.connect(filter);
      filter.connect(gain2);
      gain2.connect(ctx.destination);
      noise.start();
      noise.stop(ctx.currentTime + 0.5);
    }, 100);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🥃 CLIC - Whisky (verre cristal posé)
const playWhiskySound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Clic cristal élégant
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    osc.frequency.setValueAtTime(800, ctx.currentTime + 0.02);

    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍺 PSHIT - Bière (mousse, différent de Red Bull)
const playBeerSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Pshit plus grave et mousseux
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.6);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1500, ctx.currentTime);
    filter.Q.value = 1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 0.6);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🌿 AAAHHHH - CBD (soupir de satisfaction/relaxation)
const playCBDSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // "Aaahhhh" de satisfaction - son vocal synthétique
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    const gain2 = ctx.createGain();

    // Fondamentale (voix grave)
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.8);
    
    // Harmonique (pour le "aaah")
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(350, ctx.currentTime);
    osc2.frequency.linearRampToValueAtTime(280, ctx.currentTime + 0.8);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.3);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    
    gain2.gain.setValueAtTime(0.1, ctx.currentTime);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);

    osc.connect(gain);
    osc2.connect(gain2);
    gain.connect(ctx.destination);
    gain2.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.8);
    osc2.stop(ctx.currentTime + 0.8);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 💨 PROUT - Puffs (pet sonore) 😂
const playPuffSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Le fameux "prout" - bruit de pet 😂
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.4);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(150, ctx.currentTime);
    filter.frequency.linearRampToValueAtTime(300, ctx.currentTime + 0.1);
    filter.frequency.linearRampToValueAtTime(80, ctx.currentTime + 0.4);
    filter.Q.value = 5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

    // Oscillateur pour le côté "vibrant"
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(80, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(40, ctx.currentTime + 0.3);
    
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.15, ctx.currentTime);
    oscGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    
    osc.connect(oscGain);
    oscGain.connect(ctx.destination);

    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🐕 WOUF - Croquettes (petit chien gentil)
const playDogSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Petit aboiement aigu et mignon
    const playBark = (delay, freq) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.7, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.12);
      }, delay);
    };

    playBark(0, 400);
    playBark(150, 450);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🐱 MIAOU - Chat (miaulement)
const playCatSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Miaulement
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(700, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(900, ctx.currentTime + 0.15);
    osc.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.4);

    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍪 CROC - Biscuit (croc croc croqué)
const playBiscuitSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Bruit de craquement
    const playCrunch = (delay) => {
      setTimeout(() => {
        const noise = ctx.createBufferSource();
        noise.buffer = createWhiteNoise(ctx, 0.08);
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 2000;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        noise.start();
        noise.stop(ctx.currentTime + 0.08);
      }, delay);
    };

    playCrunch(0);
    playCrunch(100);
    playCrunch(180);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🥔 CRUNCH - Chips (croustillant)
const playChipsSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son plus croustillant que le biscuit
    const playCrunch = (delay) => {
      setTimeout(() => {
        const noise = ctx.createBufferSource();
        noise.buffer = createWhiteNoise(ctx, 0.06);
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 3500;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.06);
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        noise.start();
        noise.stop(ctx.currentTime + 0.06);
      }, delay);
    };

    playCrunch(0);
    playCrunch(70);
    playCrunch(130);
    playCrunch(200);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍬 POP - Bonbon (déballage + suçoté)
const playCandySound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son de papier froissé
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.2);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 4000;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start();
    noise.stop(ctx.currentTime + 0.2);

    // Petit "pop" de bonbon
    setTimeout(() => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.frequency.value = 600;
      g.gain.setValueAtTime(0.1, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    }, 200);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍦 SLURP - Glace (léchage/slurp)
const playIceCreamSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son de slurp/léchage
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 0.15);
    osc.frequency.linearRampToValueAtTime(150, ctx.currentTime + 0.3);
    
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🍭 ZING - Barbe à papa (magique/féérique)
const playCottonCandySound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son magique/scintillant
    const playNote = (freq, delay) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.15);
      }, delay);
    };

    playNote(800, 0);
    playNote(1000, 80);
    playNote(1200, 160);
    playNote(1600, 240);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 💄 SIFFLEMENT - Beauté (sifflement d'admiration à l'ancienne) 😏
const playBeautySound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Sifflement d'admiration "fiu-fiuuuu" 😏
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    // Montée rapide puis descente (fiu-fiuuuu)
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(1400, ctx.currentTime + 0.15);
    osc.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.25);
    osc.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.5);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.15);
    gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.35);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🏠 SPLASH - Maison/Lessive (eau qui coule + machine)
const playHouseSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son d'eau qui coule/lave
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.6);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, ctx.currentTime);
    filter.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.2);
    filter.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.6);
    filter.Q.value = 2;
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start();
    noise.stop(ctx.currentTime + 0.6);

    // Petit "ding" de machine à laver à la fin
    setTimeout(() => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1800;
      g.gain.setValueAtTime(0.1, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    }, 500);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🥤 FIZZ - Limonades/Boissons gazeuses (bulles pétillantes)
const playLemonadeSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son de bulles/pétillement frais
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.5);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(4000, ctx.currentTime);
    filter.frequency.linearRampToValueAtTime(6000, ctx.currentTime + 0.2);
    filter.frequency.linearRampToValueAtTime(3000, ctx.currentTime + 0.5);
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start();
    noise.stop(ctx.currentTime + 0.5);

    // Petites bulles qui poppent
    const playBubble = (delay, freq) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.08, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        osc.connect(g);
        g.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.05);
      }, delay);
    };

    playBubble(100, 2000);
    playBubble(180, 2400);
    playBubble(250, 1800);
    playBubble(320, 2200);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🧴 SQUISH - Hygiène (produit qui sort)
const playHygieneSound = () => {
  try {
    const ctx = createAudioContext();
    if (!ctx) return;

    // Son de produit qui sort du tube
    const noise = ctx.createBufferSource();
    noise.buffer = createWhiteNoise(ctx, 0.3);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(500, ctx.currentTime);
    filter.frequency.linearRampToValueAtTime(2000, ctx.currentTime + 0.15);
    filter.frequency.linearRampToValueAtTime(300, ctx.currentTime + 0.3);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start();
    noise.stop(ctx.currentTime + 0.3);
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 🎵 Map des sons par nom de produit
export const PRODUCT_SOUNDS = {
  // Boissons énergétiques
  'Red Bull': playRedBullSound,
  'Energy': playRedBullSound,
  'Énergétiques': playRedBullSound,
  
  // Alcools
  'Vodka': playVodkaSound,
  'Vodkas': playVodkaSound,
  'Spiritueux': playVodkaSound,
  'Vin': playWineSound,
  'Vins': playWineSound,
  'Champagne': playChampagneSound,
  'Pétillant': playChampagneSound,
  'Whisky': playWhiskySound,
  'Whiskey': playWhiskySound,
  'Rhum': playWhiskySound,
  'Bière': playBeerSound,
  'Bières': playBeerSound,
  
  // CBD/Fumeurs
  'CBD': playCBDSound,
  'CBD**': playCBDSound,
  'FFF': playCBDSound,
  'Fumeurs': playCBDSound,
  'Puffs': playPuffSound,
  'Puffs*': playPuffSound,
  
  // Animaux
  'Croquettes': playDogSound,
  'Croquettes petits chiens': playDogSound,
  'Chien': playDogSound,
  'Animaux': playDogSound,
  'Chat': playCatSound,
  'Pâtés pour chats': playCatSound,
  
  // Snacks sucrés
  'Biscuit': playBiscuitSound,
  'Biscuits': playBiscuitSound,
  'Gâteaux': playBiscuitSound,
  'Chips': playChipsSound,
  'Bonbons': playCandySound,
  'Bonbon': playCandySound,
  'Glaces': playIceCreamSound,
  'Glace': playIceCreamSound,
  'Surgelés': playIceCreamSound,
  'Barbe à papa': playCottonCandySound,
  
  // Beauté/Hygiène
  'Beauté': playBeautySound,
  'Maquillage': playBeautySound,
  'Hygiène': playHygieneSound,
  'Cosmétiques': playBeautySound,
  
  // Maison
  'Maison': playHouseSound,
  'Tout pour la maison': playHouseSound,
  'Lessive': playHouseSound,
  'Liquide vaisselle': playHouseSound,
  'Ménage': playHouseSound,
  'Entretien': playHouseSound,
  
  // Limonades/Boissons
  'Limonades': playLemonadeSound,
  'Boissons gazeuses': playLemonadeSound,
  'Boissons': playLemonadeSound,
  'Sodas': playLemonadeSound,
  'Jus': playLemonadeSound,
  'Eaux': playLemonadeSound,
};

// 🔊 Fonction principale pour jouer le son d'un produit
export const playProductSound = (productName) => {
  // Chercher le son correspondant
  const soundFn = PRODUCT_SOUNDS[productName];
  if (soundFn) {
    soundFn();
    return true;
  }
  
  // Chercher par mot-clé partiel
  for (const [key, fn] of Object.entries(PRODUCT_SOUNDS)) {
    if (productName.toLowerCase().includes(key.toLowerCase()) || 
        key.toLowerCase().includes(productName.toLowerCase())) {
      fn();
      return true;
    }
  }
  
  return false;
};

export default {
  playRedBullSound,
  playVodkaSound,
  playWineSound,
  playChampagneSound,
  playWhiskySound,
  playBeerSound,
  playCBDSound,
  playPuffSound,
  playDogSound,
  playCatSound,
  playBiscuitSound,
  playChipsSound,
  playCandySound,
  playIceCreamSound,
  playCottonCandySound,
  playBeautySound,
  playHygieneSound,
  playHouseSound,
  playLemonadeSound,
  playProductSound,
  PRODUCT_SOUNDS
};
